import type { TypedSupabaseClient } from '../client';
import {
  ConfirmedMutationRejectedError,
  type CreateSalesDocumentInput,
  type CreatePurchaseDocumentInput,
} from '@erp/data-access/interfaces';

type InvoiceCommand = CreateSalesDocumentInput | CreatePurchaseDocumentInput;
type InvoiceScope = 'sales' | 'purchases';

/** Normalize once so undefined editor properties never affect replay equality. */
function commandJson(input: InvoiceCommand): Record<string, unknown> {
  return JSON.parse(JSON.stringify(input)) as Record<string, unknown>;
}

export function invoicePaymentPayload(input: InvoiceCommand): Record<string, unknown> {
  const selected = input.invoicePayment;
  if (!selected || input.docType !== 'invoice' || !input.clientRequestId) throw new Error('invalid_invoice_payment_checkout');
  const payment = selected.payment;
  if (payment.linkedDraft) throw new Error('invoice_payment_linked_instrument_unsupported');
  return {
    payment_type: payment.paymentType,
    payment_method: payment.paymentMethod,
    third_party_type: payment.thirdPartyType,
    third_party_id: payment.thirdPartyId,
    collection_store_id: payment.collectionStoreId ?? ('originStoreId' in input ? input.originStoreId : null),
    bank_account_id: payment.bankAccountId ?? null,
    amount: payment.amount,
    payment_date: payment.paymentDate,
    reference: payment.reference ?? null,
    notes: payment.notes ?? null,
    recurring_payment_id: payment.recurringPaymentId ?? null,
    recurring_occurrence_id: payment.recurringOccurrenceId ?? null,
  };
}

export async function resumeInvoicePaymentCheckout(
  supabase: TypedSupabaseClient, scope: InvoiceScope, input: InvoiceCommand,
): Promise<string | null> {
  if (!input.clientRequestId || input.docType !== 'invoice') return null;
  if (input.invoicePayment) invoicePaymentPayload(input);
  const { data, error } = await supabase.rpc('resume_invoice_payment_checkout_atomic' as never, {
    p_scope: scope, p_client_request_id: input.clientRequestId, p_command: commandJson(input),
  } as never);
  if (error) throw error;
  return (data as string | null) ?? null;
}

export async function createInvoicePaymentCheckout(
  supabase: TypedSupabaseClient,
  scope: InvoiceScope,
  input: InvoiceCommand,
  prepared: { document: object; lines: readonly object[]; credits?: readonly object[]; transfers?: readonly object[] },
): Promise<string> {
  const { data, error } = await supabase.rpc('create_invoice_payment_checkout_atomic' as never, {
    p_scope: scope,
    p_client_request_id: input.clientRequestId,
    p_command: commandJson(input),
    p_document: prepared.document,
    p_lines: prepared.lines,
    p_credit_applications: prepared.credits ?? [],
    p_payment_transfers: prepared.transfers ?? [],
    p_payment: input.invoicePayment ? invoicePaymentPayload(input) : null,
    p_cash_session_id: input.invoicePayment?.cashSessionId ?? null,
  } as never);
  if (!error && data) return data as string;
  // The receipt exists only if BOTH writes committed. Never recognize the
  // invoice alone or return an optimistic outbox record as successful payment.
  const resumed = await resumeInvoicePaymentCheckout(supabase, scope, input);
  if (resumed) return resumed;
  const databaseError = error as { code?: string; message?: string } | null;
  if (databaseError?.code && ['22023', '23514', '23503', '42501', '40001', '23505'].includes(databaseError.code)
    && !/idempotency|resource_unavailable/.test(databaseError.message ?? '')) {
    throw new ConfirmedMutationRejectedError(error);
  }
  throw error ?? new Error('invoice_payment_checkout_result_unknown');
}
